import {Node} from "@xyflow/react";
import {
    CLUSTER_GAP,
    CLUSTER_MIN_NODES,
    HORIZONTAL_NODE_GAP
} from "./FamilyTreeLayoutConstants";
import {
    buildChildrenByParent,
    buildFamilyLayoutContext,
    buildOrderedLevelNodes,
    buildPartnerBlocks,
    buildPartnerLeftOf
} from "./FamilyTreePartnerLayout";
import {getNodeWidth} from "./FamilyTreeNodeSizing";

type TidySpacingUnit = {
    id: string;
    level: number;
    nodes: Node[];
    memberIds: string[];
    childUnitIds: string[];
    parentUnitId?: string;
};

export type CenteredTidySpacingContext = {
    levelMap: Map<number, Node[]>;
    parentMap: Map<string, string[]>;
    partnerRightOf: Map<string, string>;
    nodes: Node[];
    mainTreeNodeIds: Set<string>;
    mainMemberIds: string[];
};

function buildTidySpacingUnits(
    levelNodes: Node[],
    level: number,
    partnerRightOf: Map<string, string>,
    partnerLeftOf: Map<string, string>
) {
    const ordered = buildOrderedLevelNodes(levelNodes, partnerRightOf, partnerLeftOf);
    const blocks = buildPartnerBlocks(
        ordered,
        partnerRightOf,
        node => node.id,
        (left, right) => [left.id, right.id].join("::")
    );

    return blocks.map(block => {
        const memberIds = block.nodes.map(node => node.id);
        return {
            id: memberIds.join("::"),
            level,
            nodes: block.nodes,
            memberIds,
            childUnitIds: []
        } as TidySpacingUnit;
    });
}

function getUnitBounds(unit: TidySpacingUnit) {
    const left = Math.min(...unit.nodes.map(node => (node.position.x ?? 0) - getNodeWidth(node) / 2));
    const right = Math.max(...unit.nodes.map(node => (node.position.x ?? 0) + getNodeWidth(node) / 2));
    return {left, right, center: (left + right) / 2};
}

function shiftUnitNodes(unit: TidySpacingUnit, deltaX: number) {
    if (!Number.isFinite(deltaX) || Math.abs(deltaX) < 0.0001) return;
    unit.nodes.forEach(node => {
        node.position.x = (node.position.x ?? 0) + deltaX;
    });
}

function solveMinimumDisplacementCenters(originalCenters: number[], minimumSeparations: number[]) {
    if (originalCenters.length <= 1) return originalCenters;

    const offsets = originalCenters.map((_, index) => {
        if (index === 0) return 0;
        return minimumSeparations.slice(0, index).reduce((sum, separation) => sum + separation, 0);
    });
    const adjusted = originalCenters.map((center, index) => center - offsets[index]);
    const blocks = adjusted.map((value, index) => ({
        start: index,
        end: index,
        sum: value,
        count: 1,
        mean: value
    }));

    let blockIndex = 0;
    while (blockIndex < blocks.length - 1) {
        if (blocks[blockIndex].mean <= blocks[blockIndex + 1].mean) {
            blockIndex += 1;
            continue;
        }

        const merged = {
            start: blocks[blockIndex].start,
            end: blocks[blockIndex + 1].end,
            sum: blocks[blockIndex].sum + blocks[blockIndex + 1].sum,
            count: blocks[blockIndex].count + blocks[blockIndex + 1].count,
            mean: 0
        };
        merged.mean = merged.sum / merged.count;
        blocks.splice(blockIndex, 2, merged);
        if (blockIndex > 0) blockIndex -= 1;
    }

    const solvedAdjusted: number[] = new Array(adjusted.length);
    blocks.forEach(block => {
        for (let index = block.start; index <= block.end; index += 1) {
            solvedAdjusted[index] = block.mean;
        }
    });

    return solvedAdjusted.map((value, index) => value + offsets[index]);
}

export function applyCenteredTidySpacing(ctx: CenteredTidySpacingContext) {
    if (ctx.mainTreeNodeIds.size === 0) return;

    const {getParentsForNode, hasSharedParent} = buildFamilyLayoutContext(ctx.levelMap, ctx.partnerRightOf, ctx.parentMap);
    const partnerLeftOf = buildPartnerLeftOf(ctx.partnerRightOf);
    const childrenByParent = buildChildrenByParent(ctx.parentMap);
    const unitById = new Map<string, TidySpacingUnit>();
    const unitIdByNodeId = new Map<string, string>();
    const mainTreeNodes = ctx.nodes.filter(node => node.type === "familyMember" && ctx.mainTreeNodeIds.has(node.id));
    const enableClusterGap = mainTreeNodes.length >= CLUSTER_MIN_NODES;

    const levels = Array.from(ctx.levelMap.keys())
        .filter(level => level >= 0)
        .sort((left, right) => left - right);

    levels.forEach(level => {
        const levelNodes = (ctx.levelMap.get(level) ?? [])
            .filter(node => node.type === "familyMember" && ctx.mainTreeNodeIds.has(node.id));
        if (levelNodes.length === 0) return;

        buildTidySpacingUnits(levelNodes, level, ctx.partnerRightOf, partnerLeftOf).forEach(unit => {
            unitById.set(unit.id, unit);
            unit.memberIds.forEach(memberId => unitIdByNodeId.set(memberId, unit.id));
        });
    });

    unitById.forEach(unit => {
        const childUnitIds = new Set<string>();
        unit.memberIds.forEach(memberId => {
            const childIds = childrenByParent.get(memberId) ?? [];
            childIds.forEach(childId => {
                if (!ctx.mainTreeNodeIds.has(childId)) return;
                const childUnitId = unitIdByNodeId.get(childId);
                if (childUnitId && childUnitId !== unit.id) {
                    childUnitIds.add(childUnitId);
                }
            });
        });

        unit.childUnitIds = Array.from(childUnitIds).sort((left, right) => {
            const leftUnit = unitById.get(left);
            const rightUnit = unitById.get(right);
            return (leftUnit ? getUnitBounds(leftUnit).center : 0) - (rightUnit ? getUnitBounds(rightUnit).center : 0);
        });

        unit.childUnitIds.forEach(childUnitId => {
            const childUnit = unitById.get(childUnitId);
            if (childUnit && !childUnit.parentUnitId) {
                childUnit.parentUnitId = unit.id;
            }
        });
    });

    const getSubtreeBounds = (unitId: string): {left: number; right: number} => {
        const unit = unitById.get(unitId);
        if (!unit) return {left: 0, right: 0};

        const ownBounds = getUnitBounds(unit);
        let left = ownBounds.left;
        let right = ownBounds.right;

        unit.childUnitIds.forEach(childUnitId => {
            const childBounds = getSubtreeBounds(childUnitId);
            left = Math.min(left, childBounds.left);
            right = Math.max(right, childBounds.right);
        });

        return {left, right};
    };

    const shiftUnitSubtree = (unitId: string, deltaX: number) => {
        const unit = unitById.get(unitId);
        if (!unit || !Number.isFinite(deltaX) || Math.abs(deltaX) < 0.0001) return;

        shiftUnitNodes(unit, deltaX);
        unit.childUnitIds.forEach(childUnitId => shiftUnitSubtree(childUnitId, deltaX));
    };

    const recenterUnitOverChildren = (unitId: string) => {
        const unit = unitById.get(unitId);
        if (!unit || unit.childUnitIds.length === 0) return;

        const childBounds = unit.childUnitIds.map(getSubtreeBounds);
        const childLeft = Math.min(...childBounds.map(bounds => bounds.left));
        const childRight = Math.max(...childBounds.map(bounds => bounds.right));
        const targetCenter = (childLeft + childRight) / 2;
        const currentCenter = getUnitBounds(unit).center;
        shiftUnitNodes(unit, targetCenter - currentCenter);
    };

    const getUnitParentSet = (unitId: string) => {
        const unit = unitById.get(unitId);
        if (!unit) return new Set<string>();

        const parentIds = new Set<string>();
        unit.nodes.forEach(node => {
            getParentsForNode(node).forEach(parentId => parentIds.add(parentId));
        });
        return parentIds;
    };

    const recenterAncestorChain = (unitId?: string) => {
        let currentUnitId = unitId;
        while (currentUnitId) {
            recenterUnitOverChildren(currentUnitId);
            currentUnitId = unitById.get(currentUnitId)?.parentUnitId;
        }
    };

    const applySiblingSpacing = (unitIds: string[]) => {
        if (unitIds.length < 2) return;

        const orderedUnitIds = unitIds.slice().sort((left, right) => {
            const leftUnit = unitById.get(left);
            const rightUnit = unitById.get(right);
            return (leftUnit ? getUnitBounds(leftUnit).center : 0) - (rightUnit ? getUnitBounds(rightUnit).center : 0);
        });
        const centers = orderedUnitIds.map(unitId => getUnitBounds(unitById.get(unitId)!).center);
        const separations = orderedUnitIds.slice(0, -1).map((unitId, index) => {
            const leftUnit = unitById.get(unitId)!;
            const rightUnit = unitById.get(orderedUnitIds[index + 1])!;
            const leftCenter = getUnitBounds(leftUnit).center;
            const rightCenter = getUnitBounds(rightUnit).center;
            const leftBounds = getSubtreeBounds(unitId);
            const rightBounds = getSubtreeBounds(orderedUnitIds[index + 1]);
            const leftRightExtent = leftBounds.right - leftCenter;
            const rightLeftExtent = rightCenter - rightBounds.left;
            const leftParents = getUnitParentSet(unitId);
            const rightParents = getUnitParentSet(orderedUnitIds[index + 1]);
            const gap = enableClusterGap && !hasSharedParent(leftParents, rightParents)
                ? CLUSTER_GAP
                : HORIZONTAL_NODE_GAP;
            return leftRightExtent + gap + rightLeftExtent;
        });

        const solvedCenters = solveMinimumDisplacementCenters(centers, separations);
        orderedUnitIds.forEach((unitId, index) => {
            const currentCenter = getUnitBounds(unitById.get(unitId)!).center;
            shiftUnitSubtree(unitId, solvedCenters[index] - currentCenter);
        });
    };

    const processUnit = (unitId: string) => {
        const unit = unitById.get(unitId);
        if (!unit) return;

        unit.childUnitIds.forEach(processUnit);
        applySiblingSpacing(unit.childUnitIds);
        recenterUnitOverChildren(unitId);
    };

    const applyLevelWideSubtreeSpacing = (level: number) => {
        const orderedUnitIds = Array.from(unitById.values())
            .filter(unit => unit.level === level)
            .sort((left, right) => getUnitBounds(left).center - getUnitBounds(right).center)
            .map(unit => unit.id);
        if (orderedUnitIds.length < 2) return;

        let previousBounds = getSubtreeBounds(orderedUnitIds[0]);
        let clusterParents = getUnitParentSet(orderedUnitIds[0]);

        for (let index = 1; index < orderedUnitIds.length; index += 1) {
            const unitId = orderedUnitIds[index];
            const currentParents = getUnitParentSet(unitId);
            const isSharedCluster = hasSharedParent(clusterParents, currentParents);
            if (isSharedCluster) {
                currentParents.forEach(parentId => clusterParents.add(parentId));
            } else {
                clusterParents = new Set(currentParents);
            }

            const gap = enableClusterGap && !isSharedCluster ? CLUSTER_GAP : HORIZONTAL_NODE_GAP;
            const currentBounds = getSubtreeBounds(unitId);
            const requiredLeft = previousBounds.right + gap;
            if (currentBounds.left < requiredLeft) {
                const deltaX = requiredLeft - currentBounds.left;
                shiftUnitSubtree(unitId, deltaX);
                recenterAncestorChain(unitById.get(unitId)?.parentUnitId);
                previousBounds = {
                    left: currentBounds.left + deltaX,
                    right: currentBounds.right + deltaX
                };
                continue;
            }

            previousBounds = currentBounds;
        }
    };

    const rootUnitIds = Array.from(unitById.values())
        .filter(unit => !unit.parentUnitId)
        .sort((left, right) => getUnitBounds(left).center - getUnitBounds(right).center)
        .map(unit => unit.id);

    rootUnitIds.forEach(processUnit);
    const maxLevel = Math.max(...levels, 0);
    for (let level = maxLevel; level >= 1; level -= 1) {
        applyLevelWideSubtreeSpacing(level);
    }
    applySiblingSpacing(rootUnitIds);

    const mainNodes = ctx.nodes.filter(node => ctx.mainMemberIds.includes(node.id));
    if (mainNodes.length === 0) return;
    const mainLeft = Math.min(...mainNodes.map(node => (node.position.x ?? 0) - getNodeWidth(node) / 2));
    const mainRight = Math.max(...mainNodes.map(node => (node.position.x ?? 0) + getNodeWidth(node) / 2));
    const mainCenter = (mainLeft + mainRight) / 2;
    if (!Number.isFinite(mainCenter) || Math.abs(mainCenter) < 0.0001) return;

    ctx.nodes.forEach(node => {
        if (!ctx.mainTreeNodeIds.has(node.id)) return;
        node.position.x = (node.position.x ?? 0) - mainCenter;
    });
}